import React, { useState, useRef, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';

import DiagnosticProduct from './DiagnosticProduct';
import axios from 'axios';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import { classNames } from 'primereact/utils';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

const Quoter = ({ quotation, payment }) => {
    const [products, setProducts] = useState([]);
    const [validating, setValidating] = useState(false);
    const productsData = useRef([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [saving, setSaving] = useState(false);

    const [prescriptionsPrice, setPrescriptionsPrice] = useState(payment.precio_receta);
    const [shippingPrice, setShippingPrice] = useState(payment.precio_depacho);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setLoadingProducts(true);
        axios
            .get('/quotations/' + quotation.id_registro + '/products')
            .then((response) => {
                setProducts(response.data);
                setLoadingProducts(false);
            })
            .catch(() => {
                console.log('Error cargando datos iniciales.');
            });
    }, []);

    const createProducts = () => {
        const tabs = [];

        for (let i = 0; i < products.length; i++) {
            const onProductDataChange = (formik) => {
                productsData.current[i] = formik;
                calculatePrescriptionsPrices();
            };

            tabs.push(
                <TabPanel header={'Receta ' + (i + 1)} key={i}>
                    <DiagnosticProduct
                        product={products[i]}
                        quotation={quotation}
                        validating={validating}
                        onDataChange={onProductDataChange}
                        productsLen={products.length}
                    />
                </TabPanel>
            );
        }

        return (
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} renderActiveOnly={false}>
                {tabs}
            </TabView>
        );
    };

    const calculatePrescriptionsPrices = () => {
        const prescriptionsPrice = productsData.current.reduce((previous, current) => previous + current.values.totalPrice, 0);
        setPrescriptionsPrice(prescriptionsPrice);
        formikRef.current.setFieldValue('prescriptionsPrice', prescriptionsPrice);
    };

    const onShippingPriceChange = (e) => {
        setShippingPrice(e.value);
        formikRef.current.setFieldValue('shippingPrice', e.value);
    };

    const initialValues = {
        prescriptionsPrice: payment.precio_receta || 0,
        shippingPrice: payment.precio_depacho || 0,
        totalPrice: payment.precio_total || 0,
    };

    const validateForm = () => {
        let errors = {};
        return errors;
    };

    const formikRef = useRef(null);
    const toast = useRef(null);

    useEffect(() => {
        if (prescriptionsPrice || shippingPrice) {
            formikRef.current.setFieldValue('totalPrice', (prescriptionsPrice || 0) + (shippingPrice || 0));
        }
    }, [prescriptionsPrice, shippingPrice]);

    const saveQuotation = () => {
        setValidating(true);
        formikRef.current.submitForm();
    };

    const onSubmit = () => {
        const allProductsFormsValid = productsData.current.reduce((previous, current) => previous && current.isValid, true);

        if (!allProductsFormsValid) {
            for (let i = 0; i < productsData.current.length; i++) {
                if (!productsData.current[i].isValid) {
                    setActiveIndex(i);
                    break;
                }
            }
        }

        if (formikRef.current.isValid && allProductsFormsValid) {
            const saveQuotation = (reloadPage) => {
                setSaving(true);
                axios
                    .put('/quotations/' + quotation.id_registro + '/quotation', {
                        quotation: formikRef.current.values,
                        products: productsData.current.map((val) => val.values),
                    })
                    .then(() => {
                        toast.current.clear();
                        toast.current.show({ severity: 'success', detail: 'Enviado correctamente' });
                        if (reloadPage) {
                            window.location.reload(true);
                        }
                    })
                    .catch(() => {
                        toast.current.clear();
                        toast.current.show({ severity: 'error', detail: 'Ha ocurrido un error al intentar grabar' });
                    })
                    .finally(() => {
                        setSaving(false);
                    });
            };

            if (quotation.estado !== 'Pagado') {
                confirmDialog({
                    message: 'Se guardará la cotización y quedará marcado como "Cotizado". ¿Desea continuar?',
                    header: 'Guardar cotización',
                    acceptLabel: 'Sí',
                    accept: () => {
                        saveQuotation(true);
                    },
                });
            } else {
                saveQuotation(false);
            }
        }
    };

    return (
        <>
            <Toast ref={toast} position="bottom-right" />
            <ConfirmDialog acceptLabel="Sí" />
            <Panel header="Detalle de productos" toggleable>
                <div>
                    {loadingProducts ? (
                        <div className="text-center">
                            <ProgressSpinner style={{ width: '40px' }} />
                        </div>
                    ) : (
                        createProducts()
                    )}
                </div>
            </Panel>

            <Divider />
            <Panel header="Cotización">
                <Formik innerRef={formikRef} initialValues={initialValues} validate={validateForm} onSubmit={onSubmit}>
                    <Form>
                        <div className="row">
                            <div className="col-12 col-lg-4">
                                <label htmlFor="prescriptionsPrice">Precio Recetas:</label>
                                <Field name="prescriptionsPrice">
                                    {({ field }) => (
                                        <div className={classNames('p-inputgroup', 'form-component')}>
                                            <span className="p-inputgroup-addon">$</span>
                                            <InputNumber {...field} locale="es-CL" readOnly disabled={quotation.estado === 'Pagado'} />
                                        </div>
                                    )}
                                </Field>
                            </div>
                            <div className="col-12 col-lg-4">
                                <label htmlFor="shippingPrice">Precio Despacho:</label>
                                <Field name="shippingPrice">
                                    {({ field }) => (
                                        <div className={classNames('p-inputgroup', 'form-component')}>
                                            <span className="p-inputgroup-addon">$</span>
                                            <InputNumber
                                                {...field}
                                                onChange={() => {}}
                                                onValueChange={onShippingPriceChange}
                                                locale="es-CL"
                                                min={0}
                                                placeholder="Precio despacho"
                                                disabled={quotation.estado === 'Pagado'}
                                            />
                                        </div>
                                    )}
                                </Field>
                                <ErrorMessage name="shippingPrice" component="div" className="p-error" />
                            </div>
                            <div className="col-12 col-lg-4">
                                <label htmlFor="totalPrice">Precio Total:</label>
                                <Field name="totalPrice">
                                    {({ field }) => (
                                        <div className={classNames('p-inputgroup', 'form-component')}>
                                            <span className="p-inputgroup-addon">$</span>
                                            <InputNumber {...field} locale="es-CL" readOnly disabled={quotation.estado === 'Pagado'} />
                                        </div>
                                    )}
                                </Field>
                            </div>

                            <div className="d-flex flex-column flex-lg-row justify-content-center mt-5 gap-3">
                                <Button type="button" severity="secondary" label="Grabar cotización" onClick={saveQuotation} loading={saving} />
                            </div>
                        </div>
                    </Form>
                </Formik>
            </Panel>
        </>
    );
};

export default Quoter;
